import * as d3 from 'd3';
import _ from 'lodash';
import { getCurrentTransform } from './mapElementHelper';
import { BED_HEIGHT, BED_MARGIN, BED_WIDTH } from '../../pages/ward/map/mapState';
import {
    AgyleAreaZoneIcon,
    AgyleBedIcon,
    AgyleChairIcon,
    AgyleListIcon,
    AgyleNamedIcon,
    AgyleSquareIcon,
    AgyleTrolleyIcon,
    AgyleZoneCategoryIcon,
} from '../../components/icons/agyle';
import React from 'react';
import Formatter from '../formatter';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyTwoToneIcon from '@mui/icons-material/ContentCopyTwoTone';
import AddCircleIcon from '@mui/icons-material/AddCircle';

// Helper method to create ward map
const removeOldMap = (element) => {
    d3.select(element).selectAll('svg').remove('svg');
};
export const DEFAULT_CORDS = { x: null, y: null };
export const GRID_SIZE = 65;
export const MAP_MAX_ZOOM = 5;
export const MAP_MIN_ZOOM = 0.3;

export const MAP_TYPE = {
    INPATIENT: 'INPATIENT',
    ED: 'EMERGENCY_DEPARTMENT',
    OUTPATIENT: 'OUTPATIENT',
    SDEC: 'SDEC',
    UTC: 'UTC',
};

export const MAP_ZOOM_LEVEL = {
    FULL: 'full',
    BIG: 'big',
    MEDIUM: 'medium',
    SMALL: 'small',
};

export const MAP_ZOOM_LEVEL_NAME = {
    [MAP_ZOOM_LEVEL.FULL]: 'Full Card View',
    [MAP_ZOOM_LEVEL.BIG]: 'Reduced Card View',
    [MAP_ZOOM_LEVEL.MEDIUM]: 'Minimal Card View',
    // [MAP_ZOOM_LEVEL.SMALL]: 'Small Card View',
};

export const MAP_ZOOM_SIMPLE_LEVEL_NAME = {
    [MAP_ZOOM_LEVEL.FULL]: 'Full',
    [MAP_ZOOM_LEVEL.BIG]: 'Reduced',
    [MAP_ZOOM_LEVEL.MEDIUM]: 'Minimal',
    // [MAP_ZOOM_LEVEL.SMALL]: 'Small',
};

export const checkEDMapType = (wardId) => {
    const type = wardId.split('_')[0];
    if (type === 'ED') {
        return MAP_TYPE.ED;
    } else if (type === 'SDEC') {
        return MAP_TYPE.SDEC;
    } else if (type === 'UTC') {
        return MAP_TYPE.UTC;
    } else {
        return null;
    }
};

export const createMap = (element, onChangeScale, currentScale, width, height) => {
    removeOldMap(element); // Reset map
    const zoom = d3.zoom().on('zoom', handleZoom);
    const scale = getCurrentTransform(currentScale);
    const initZoom = d3.zoomIdentity.translate(scale.x, scale.y).scale(scale.k);

    return d3
        .select(element)
        .append('svg')
        .attr('id', `ward-map`)
        .attr('width', width - 50)
        .attr('height', height - 100)
        .style('overscroll-behavior', 'none')
        .call(zoom)
        .call(zoom.transform, initZoom)
        .on('dblclick.zoom', null);

    function handleZoom(e) {
        if (e?.sourceEvent?.type !== 'dblclick') {
            d3.select('.context-menu').remove(); // Close all context menu when scaling
            d3.selectAll('svg g')
                .filter(function () {
                    return !this.classList.contains('agyle-menu');
                })
                .attr('transform', e.transform);
            onChangeScale();
        }
    }
};

export const renderBorderAndGrid = (svg, isGrid, scale, lightTheme, map) => {
    const BORDERS = [
        { begin: [0, 0], end: [0, Number(map.height)] },
        { begin: [Number(map.width), 0], end: [Number(map.width), Number(map.height)] },
        { begin: [-2, 0], end: [Number(map.width) + 2, 0] },
        { begin: [-2, Number(map.height)], end: [Number(map.width) + 2, Number(map.height)] },
    ];

    const grid = svg.append('g').attr('id', 'grid').attr('transform', scale).attr('data-zoom', scale);

    if (isGrid) {
        for (let i = -150 * GRID_SIZE; i < 200 * GRID_SIZE; i += GRID_SIZE) {
            d3.select("g[id='grid']")
                .append('line')
                .style('stroke', lightTheme ? '#c7c7c7' : '#464646FF')
                .style('stroke-width', 2)
                .attr('x1', i)
                .attr('y1', -150 * GRID_SIZE)
                .attr('x2', i)
                .attr('y2', 200 * GRID_SIZE);
        }

        for (let i = -150 * GRID_SIZE; i < 200 * GRID_SIZE; i += GRID_SIZE) {
            d3.select("g[id='grid']")
                .append('line')
                .style('stroke', lightTheme ? '#c7c7c7' : '#464646FF')
                .style('stroke-width', 2)
                .attr('x1', -150 * GRID_SIZE)
                .attr('y1', i)
                .attr('x2', 200 * GRID_SIZE)
                .attr('y2', i);
        }
    }

    _.forEach(BORDERS, (br) => {
        const { end, begin } = br;
        grid.append('line')
            .style('stroke', '#787878')
            .style('stroke-width', 4)
            .style('stroke-dasharray', 5)
            .attr('x1', begin[0])
            .attr('y1', begin[1])
            .attr('x2', end[0])
            .attr('y2', end[1]);
    });
};

export const renderMainCenterText = (svg, text, width, height) => {
    svg.append('text')
        .text(text)
        .attr('x', width / 2)
        .attr('y', (height * 0.9) / 2)
        .attr('font-size', 30)
        .attr('text-anchor', 'middle')
        .style('fill', '#999999');
};

export const renderAreaName = (areaSVG, obj, text, type, id, lightMode) => {
    const rectHeight = 17;
    let width = obj.count * BED_WIDTH + BED_MARGIN * 4 + (obj.count - 1) * BED_MARGIN;

    if (obj.isHorizontalList) {
        width = Number(obj.width) + BED_MARGIN * 4;
    }

    // Background zone name
    areaSVG
        .append('rect')
        .attr('id', id)
        .attr('bkId', id)
        .attr('type', type)
        .attr('subtype', 'Background Rect')
        .attr('x', obj.x - 5)
        .attr('y', obj.y - rectHeight)
        .attr('width', width)
        .attr('height', rectHeight)
        .attr('fill', lightMode ? '#BFC2C7' : '#363636');

    areaSVG
        .append('text')
        .attr('id', id)
        .attr('type', 'zone')
        .attr('x', obj.x)
        .attr('y', obj.y - 3)
        .text(text)
        .style('font-weight', 'bold')
        .style('font-size', '12px')
        .style('fill', lightMode ? '#00000099' : '#ffffff99');
};

const _buttons = (id, icon, method, active) => {
    return { id, icon, method, active };
};

export const renderObjectMapButtons = (svg, obj, id, scale, handleCopy, handleDelete, handleEdit, handleAdd, isBed) => {
    const menuLayer = svg.append('g').attr('class', 'menu-layer').attr('transform', scale);

    const buttons = [
        _buttons('copy', Formatter.getSvgPath(<ContentCopyTwoToneIcon />), handleCopy, !!handleCopy),
        _buttons('delete', Formatter.getSvgPath(<DeleteIcon />), handleDelete, !!handleDelete),
        _buttons('edit', Formatter.getSvgPath(<EditIcon />), handleEdit, !!handleEdit),
        _buttons('add', Formatter.getSvgPath(<AddCircleIcon />), handleAdd, !!handleAdd),
    ];
    const activeButtons = _.filter(buttons, (btn) => btn.active);
    const btnDimension = 30;
    const width = 50;
    const height = 41 * activeButtons.length;

    menuLayer
        .append('rect')
        .attr('id', id)
        .attr('menu', true)
        .attr('noDrag', true)
        .attr('x', obj.x - width)
        .attr('y', obj.y - 1)
        .attr('width', width)
        .attr('height', height)
        .style('fill', '#4d7ff3');

    _.forEach(activeButtons, (btn, index) => {
        let y = obj.y + 7;

        if (index > 0) {
            y += 10 * index + btnDimension * index;
        }

        const sbgBtn = menuLayer
            .append('svg')
            .attr('viewBox', `0 0 24 24`)
            .attr('x', obj.x - width / 2 - btnDimension / 2)
            .attr('y', y)
            .attr('width', btnDimension)
            .attr('height', btnDimension)
            .on('click', () => btn.method(isBed ? obj : id, btn.id !== 'add'))
            .append('path')
            .attr('id', id)
            .attr('d', btn.icon)
            .attr('menu', true)
            .attr('fill', '#d4d4d4')
            .attr('transform', scale.current)
            .on('click', () => btn.method(isBed ? obj : id, btn.id !== 'add'));

        sbgBtn.on('mouseover', () => {
            sbgBtn.attr('fill', '#ffffff');
        });
        sbgBtn.on('mouseout', () => {
            sbgBtn.attr('fill', '#d4d4d4');
        });
    });
};

export const renderBed = (areaSVG, bed, x, y, id, outlineOffset, lightMode) => {
    const lineHeight = 25;
    const line1X1 = x + outlineOffset;
    const line1Y1 = y + lineHeight;
    const line1X2 = x + BED_WIDTH - outlineOffset;
    const line1Y2 = y + lineHeight;
    const separatorHeight = 2;

    const bedAvailable = bed?.available;
    const bedType = bed?.subtype;

    // Get bed color
    let color = lightMode ? '#ffffff99' : '#4b4b4b';
    let colorText = lightMode ? '#00000099' : '#ffffff99';
    if (bedType === 'planned') color = lightMode ? '#ffffff' : '#222425';
    if (bedType === 'unplanned') {
        color = '#634101';
        colorText = '#ffffff99';
    }
    if (!bedAvailable) {
        color = '#70191c';
        colorText = '#ffffff99';
    }

    // Render bed title background
    areaSVG
        .append('rect')
        .attr('id', id)
        .attr('noDrag', true)
        .attr('x', x + outlineOffset)
        .attr('y', y + outlineOffset)
        .attr('width', BED_WIDTH - outlineOffset * 2)
        .attr('height', lineHeight - outlineOffset)
        .attr('fill', color);

    // Render bed title separator
    areaSVG
        .append('line')
        .attr('id', id)
        .attr('x1', line1X1)
        .attr('y1', line1Y1)
        .attr('x2', line1X2)
        .attr('y2', line1Y2)
        .attr('stroke', lightMode ? '#8B8B8B' : '#0D0D0D')
        .attr('stroke-width', separatorHeight)
        .attr('noDrag', true);

    // Render bed title
    areaSVG
        .append('text')
        .attr('id', id)
        .attr('type', 'bed')
        .attr('x', x + outlineOffset + 5)
        .attr('y', y + outlineOffset + lineHeight / 1.9)
        .text(bed?.name || '')
        .style('font-size', '11px')
        .style('fill', colorText);

    // Add side room
    if (bed?.sideRoom) {
        areaSVG
            .append('text')
            .attr('id', id)
            .attr('type', 'bed')
            .attr('x', x + BED_WIDTH - 25)
            .attr('y', y + outlineOffset + lineHeight / 1.9)
            .text('SR')
            .style('font-size', '11px')
            .style('fill', colorText);
    }
};

export const renderSelectBed = (areaSVG, bed, area, color, handleSelectSpaceFromMenu, setDroppedSpace) => {
    // Rectangle dimensions
    const rectWidth = BED_WIDTH / 6;
    const rectHeight = BED_HEIGHT / 4;
    const xPos = bed.x + BED_WIDTH / 2.4;
    const yPos = bed.y + BED_HEIGHT / 2.7;

    const lineOffset = 15;

    // First line dimensions horizontal
    const line1X1 = xPos + lineOffset + 1.2;
    const line1Y1 = yPos + rectHeight / 2;
    const line1X2 = xPos + rectWidth - lineOffset - 1.2;
    const line1Y2 = yPos + rectHeight / 2;

    // Second line dimensions vertical
    const line2X1 = xPos + rectWidth / 2;
    const line2Y1 = yPos + lineOffset / 1.5;
    const line2X2 = xPos + rectWidth / 2;
    const line2Y2 = yPos + rectHeight - lineOffset / 1.5;

    areaSVG
        .append('rect')
        .attr('id', area.__id)
        .attr('uniqId', bed.uniqId)
        .attr('noDrag', true)
        .attr('x', xPos)
        .attr('y', yPos)
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .attr('fill', color)
        .style('outline-style', 'solid')
        .style('outline-color', '#8B8B8B')
        .style('outline-width', '1px')
        .on('click', handleSelectSpaceFromMenu.bind(this, bed, area))
        .on('mouseover', (_e) => setDroppedSpace(bed));

    areaSVG
        .append('line')
        .attr('x1', line1X1)
        .attr('y1', line1Y1)
        .attr('x2', line1X2)
        .attr('y2', line1Y2)
        .attr('stroke', '#8B8B8B')
        .attr('stroke-width', 1)
        .attr('noDrag', true);

    areaSVG
        .append('line')
        .attr('x1', line2X1)
        .attr('y1', line2Y1)
        .attr('x2', line2X2)
        .attr('y2', line2Y2)
        .attr('stroke', '#8B8B8B')
        .attr('stroke-width', 1)
        .attr('noDrag', true);
};

export const contextMenu = (svg, x, y, lightMode, menu, customWidth) => {
    const items = menu || [];
    let height,
        width,
        margin = 0.1;

    d3.select('.context-menu').remove();
    scaleItems();

    // Draw the menu
    svg.append('g')
        .attr('class', 'context-menu')
        .selectAll('tmp')
        .data(items)
        .enter()
        .append('g')
        .attr('class', 'menu-entry')
        .style('cursor', 'pointer')
        .on('mouseover', function () {
            d3.select(this)
                .select('rect')
                .style('fill', lightMode ? '#a1a1a1' : '#626262');
        })
        .on('mouseout', function () {
            d3.select(this)
                .select('rect')
                .style('fill', lightMode ? '#d5d5d5' : '#363636');
        })
        .on('click', (_event, data) => {
            if (data.action) data.action();
        });

    svg.selectAll('.menu-entry')
        .append('rect')
        .attr('x', x)
        .attr('y', function (d, i) {
            return y + i * height;
        })
        .attr('width', width)
        .attr('height', height)
        .style('fill', lightMode ? '#dee1e5' : '#363636');

    d3.selectAll('.menu-entry')
        .append('text')
        .text(function (d) {
            return d.title ? d.title : 'Err';
        })
        .attr('x', x)
        .attr('y', function (d, i) {
            return y + i * height - 2;
        })
        .attr('dy', height - margin / 2)
        .attr('dx', margin)
        .style('fill', lightMode ? '#272727' : '#f1f1f1')
        .style('font-size', '13')
        .style('font-weight', 'bold');

    // Other interactions
    d3.select('body').on('click', function () {
        d3.select('.context-menu').remove();
    });

    function scaleItems() {
        svg.selectAll('tmp')
            .data(items)
            .enter()
            .append('text')
            .text(function (d) {
                return d.title ? d.title : 'Err';
            })
            .style('fill', 'steelblue')
            .style('font-size', '13')
            .attr('x', -1000)
            .attr('y', -1000)
            .attr('class', 'tmp');

        width = customWidth || 150;
        margin = margin * width;
        width = width + 2 * margin;
        height = 20 + margin / 2;

        // cleanup
        d3.selectAll('.tmp').remove();
    }
};

const getBackgroundCords = (zone, lightMode, isBlackBox) => {
    const margin = BED_MARGIN * (isBlackBox ? 2 : 4);
    let width = BED_WIDTH + margin;
    let height = BED_HEIGHT + margin;
    let color = lightMode ? '#BFC2C7' : '#363636';
    let x = zone.x - BED_MARGIN * (isBlackBox ? 1 : 2);
    let y = zone.y - BED_MARGIN * (isBlackBox ? 1 : 2);

    // If zone is a List
    if (zone.direction) {
        const isHorizontal = zone.direction === 'horizontal';
        width = Number(isHorizontal ? Number(zone.width) + margin : width);
        height = Number(!isHorizontal ? Number(zone.height) + margin : height);
    }

    return { width, height, color, x, y };
};

export const renderBackground = (svg, array, id, type, lightMode) => {
    // Background box
    _.forEach(array, (zone) => {
        const { width, height, color, x, y } = getBackgroundCords(zone, lightMode, false);
        svg.append('rect')
            .attr('id', id)
            .attr('bkId', id)
            .attr('type', type)
            .attr('subtype', 'Background Rect')
            .attr('x', x)
            .attr('y', y)
            .attr('width', width)
            .attr('height', height)
            .attr('fill', color);
    });

    // Black border box
    _.forEach(array, (zone) => {
        const { width, height, x, y } = getBackgroundCords(zone, lightMode, true);
        svg.append('rect')
            .attr('id', id)
            .attr('bkId', id)
            .attr('type', type)
            .attr('subtype', 'Background Rect')
            .attr('x', x)
            .attr('y', y)
            .attr('width', width)
            .attr('height', height)
            .attr('fill', lightMode ? '#ACAFB3' : '#212427');
    });
};

export const renderAddingNewSpace = (svg, direction, lightMode, area, bed, addArea, outlineOffset) => {
    const pos = getVertices(direction.dir, bed);
    const color = lightMode ? '#BFC2C7' : '#363636';
    svg.append('rect')
        .attr('fill', color)
        .attr('id', area.__id)
        .attr('type', area.type)
        .attr('uniqId', bed.uniqId)
        .attr('outline', true)
        .attr('x', pos.topX - BED_WIDTH)
        .attr('y', pos.topY)
        .attr('width', BED_WIDTH)
        .attr('height', BED_HEIGHT)
        .style('outline-style', 'dashed')
        .style('outline-color', lightMode ? '#363636' : '#8b8d8f')
        .style('outline-offset', `-13px`)
        .style('outline-width', '1px')
        .on('click', addArea.bind(this, area.__id, direction.dir, bed.x, bed.y));

    // Rectangle dimensions
    const rectWidth = BED_WIDTH / 6;
    const rectHeight = BED_HEIGHT / 4;
    const xPos = pos.topX - BED_WIDTH + BED_WIDTH / 2.4;
    const yPos = pos.topY + BED_HEIGHT / 2.7;

    const lineOffset = 15;

    // First line dimensions horizontal
    const line1X1 = xPos + lineOffset + 1.2;
    const line1Y1 = yPos + rectHeight / 2;
    const line1X2 = xPos + rectWidth - lineOffset - 1.2;
    const line1Y2 = yPos + rectHeight / 2;

    // Second line dimensions vertical
    const line2X1 = xPos + rectWidth / 2;
    const line2Y1 = yPos + lineOffset / 1.5;
    const line2X2 = xPos + rectWidth / 2;
    const line2Y2 = yPos + rectHeight - lineOffset / 1.5;

    svg.append('rect')
        .attr('id', area.__id)
        .attr('uniqId', bed.uniqId)
        .attr('noDrag', true)
        .attr('x', xPos)
        .attr('y', yPos)
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .attr('fill', color)
        .style('outline-style', 'solid')
        .style('outline-color', '#8B8B8B')
        .style('outline-width', '1px')
        .on('click', addArea.bind(this, area.__id, direction.dir, bed.x, bed.y));

    svg.append('line')
        .attr('x1', line1X1)
        .attr('y1', line1Y1)
        .attr('x2', line1X2)
        .attr('y2', line1Y2)
        .attr('stroke', '#8B8B8B')
        .attr('stroke-width', 1)
        .attr('noDrag', true);

    svg.append('line')
        .attr('x1', line2X1)
        .attr('y1', line2Y1)
        .attr('x2', line2X2)
        .attr('y2', line2Y2)
        .attr('stroke', '#8B8B8B')
        .attr('stroke-width', 1)
        .attr('noDrag', true);
};

// Method that returns vertices of a new bed depending on the direction
export const getVertices = (dir, bed, withoutMargin) => {
    const { x, y } = bed;
    const beginCords = getCordForDirection(dir, x, y, withoutMargin);
    const endCords = {
        topX: beginCords.x + BED_WIDTH,
        topY: beginCords.y,
        bottomX: beginCords.x + BED_WIDTH,
        bottomY: beginCords.y + BED_HEIGHT,
    };

    return {
        ...beginCords,
        ...endCords,
    };
};

// Method that returns vertices of a new bed depending on the direction
export const highlightObject = (svgMap, selectedObject, type) => {
    let pos;
    let id;
    if (type === 'bed') {
        pos = _getPositionForHighlight(selectedObject);
        id = selectedObject?.areaId;
    } else if (type === 'list') {
        pos = _getPositionForHighlight(selectedObject[0], true);
        id = selectedObject[0]?.areaId;
    } else {
        const allPos = [];
        const onlyEdge = [];

        for (const element of selectedObject) {
            const cords = _getPositionForHighlight(element);
            if (!id) id = element?.areaId;

            allPos.push(cords[0]);
            allPos.push(cords[1]);
            allPos.push(cords[2]);
            allPos.push(cords[3]);
        }

        function checkOuterEdges(cordToCheck, ignoreIndex) {
            let isOuter = true;
            _.forEach(allPos, (cord, index) => {
                if (!isOuter) return;
                if (index !== ignoreIndex) {
                    let check1 =
                        (cordToCheck[0] === cord[0] && cordToCheck[1] === cord[1]) ||
                        (cordToCheck[0] === cord[2] && cordToCheck[1] === cord[3]);
                    let check2 =
                        (cordToCheck[2] === cord[0] && cordToCheck[3] === cord[1]) ||
                        (cordToCheck[2] === cord[2] && cordToCheck[3] === cord[3]);
                    isOuter = !(check1 && check2);
                }
            });
            return isOuter;
        }

        allPos.forEach((cord, index) => {
            if (checkOuterEdges(cord, index)) onlyEdge.push(cord);
        });

        pos = onlyEdge;
    }

    _.forEach(pos, (cord) => {
        d3.select(`g[id='${id}']`)
            .append('line')
            .style('stroke', '#4d7ff3')
            .style('stroke-width', 2)
            .attr('x1', cord[0])
            .attr('y1', cord[1])
            .attr('x2', cord[2])
            .attr('y2', cord[3])
            .attr('noDrag', true);
    });
};

const _getPositionForHighlight = (obj, isList) => {
    let x = Number(obj?.x);
    let y = Number(obj?.y);
    let width = BED_WIDTH;
    let height = BED_HEIGHT;

    if (isList) {
        const outlineOffset = 10;
        x += outlineOffset;
        y += outlineOffset;

        if (obj.width) {
            width = Number(obj.width) - outlineOffset * 2;
            height -= outlineOffset * 2;
        }
        if (obj.height) {
            height = Number(obj.height) - outlineOffset * 2;
            width -= outlineOffset * 2;
        }
    }
    return [
        [x, y, x + width + 1, y],
        [x + width, y, x + width, y + height + 1],
        [x + width, y + height, x - 1, y + height],
        [x, y + height, x, y - 1],
    ];
};

export const getCordForDirection = (dir, x, y, withoutMargin) => {
    const margin = !withoutMargin ? BED_MARGIN : 0;
    let dirX = x;
    let dirY = y;

    switch (dir) {
        case 'TOP':
            dirY -= BED_HEIGHT + margin;
            break;
        case 'BOTTOM':
            dirY += BED_HEIGHT + margin;
            break;
        case 'LEFT':
            dirX -= BED_WIDTH + margin;
            break;
        case 'RIGHT':
            dirX += BED_WIDTH + margin;
            break;
        default:
            break;
    }
    return { x: dirX, y: dirY };
};

export const upsert = (array, element) => {
    const i = array.findIndex((_element) => _element.__id === element.__id);
    if (i > -1) array[i] = element;
    else array.push(element);
};

export const getMapElementIcon = (el) => {
    if (!el) return <AgyleSquareIcon />;

    switch (el.type) {
        case 'list':
            return <AgyleListIcon />;
        case 'area':
            return <AgyleZoneCategoryIcon />;
        case 'bed':
            return <AgyleBedIcon />;
        case 'trolley':
            return <AgyleTrolleyIcon />;
        case 'chair':
            return <AgyleChairIcon />;
        default:
            if (el?.envObject) {
                const name = el.type || 'envCategory';
                return <AgyleNamedIcon name={name} />;
            }
            // For toolbar
            if (el.hasOwnProperty('bedId')) {
                return el?.bedId ? <AgyleBedIcon /> : <AgyleSquareIcon />;
            }
            return <AgyleAreaZoneIcon />;
    }
};

export const createMapCode = (wardsId, wards) => {
    let code = _.join(wardsId, '_');
    let valid = false;
    while (!valid) {
        let uniq = Math.random().toString(36).substring(2, 8);
        code = `${_.join(wardsId, '_')}_${uniq}`;
        valid = _.findIndex(wards, { code }) === -1;
    }
    return code;
};

export const generateObjectId = (value, wardId, objectType, areas, beds, envType) => {
    const valueId = wardId + '_' + value;
    const id = objectType !== 'bed' ? _generateForArea(valueId, objectType, envType) : _.snakeCase(valueId);

    const checkUnique = (id) => {
        let index = _.findIndex(objectType !== 'bed' ? areas : beds, { hl7Code: id });
        return index === -1;
    };

    // Check if ID is unique if not increment value by one and add to id as string
    return checkUnique(id) ? id : _incrementId(value, wardId, objectType, areas, beds, envType);
};

const _incrementId = (value, wardId, objectType, areas, beds, envType) => {
    const lastDash = value.lastIndexOf('_');
    const text = value.substring(lastDash + 1);

    if (!_.isNaN(Number(text))) {
        let num = Number(text);
        num += 1;
        return generateObjectId(value.slice(0, lastDash) + `_${num}`, wardId, objectType, areas, beds, envType);
    } else {
        return generateObjectId(value + '_1', wardId, objectType, areas, beds, envType);
    }
};

const _generateForArea = (value, objectType, envType) => {
    let type;
    switch (objectType) {
        case 'list':
            type = 'wlist_';
            break;

        case 'env':
            type = `env_${envType ? envType + '_' : ''}`;
            break;

        default:
            type = '';
            break;
    }
    return `${type}${_.snakeCase(value)}`;
};

export const getLocationChipColor = (theme, ward) => {
    let backgroundColor;
    let color = '#ffffff';
    if (ward.type === MAP_TYPE.ED) {
        const newType = checkEDMapType(ward?.wardsId?.[0]);
        switch (newType) {
            case MAP_TYPE.SDEC:
                backgroundColor = theme.palette.attendanceType.sdec;
                break;
            case MAP_TYPE.UTC:
                backgroundColor = theme.palette.attendanceType.utc;
                color = '#202020';
                break;
            case MAP_TYPE.ED:
            default:
                backgroundColor = theme.palette.attendanceType.ed;
        }
    } else {
        backgroundColor = theme.palette.attendanceType.ip;
    }
    return { backgroundColor, color };
};
